import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import {
  ProductTypeSerializer,
  ProductVariantSerializer,
} from "@app-contexts/public/features/products/data/types/products";
import CartTimer from "@shared/components/application/data-display/CartTimer/CartTimer.web";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import CardImage from "@ui-application/media/CardImage/CardImage.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useEffect, useState } from "react";
import useMountEffect from "@shared/hooks/useMountEffect/useMountEffect";
import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import { AnalyticsService } from "@shared/services/analytics";
import { isProductPackageChild } from "@shared/modules/basket/utils/helpers";
import { AddonProductTabs, AddonProductTabsProps } from "./ProductAddonsTabs";

export interface VariantQuantity {
  variant: ProductVariantSerializer;
  quantity: number;
}

export type VariantQuantityMap = Record<string, VariantQuantity>;

export interface ProductAddonsModalContentProps {
  product: ProductTypeSerializer;
  showCartTimer?: boolean;
  onChange?: (change: VariantQuantityMap) => void;
  hideDescription?: boolean;
}

export const findVariant = (
  product: ProductTypeSerializer,
  variantId: number
) => {
  return product.product_attributes?.find((v) => v.id === variantId);
};

export const loadProductCountsFromBasket = (
  itemGroups: TicketItemGroupType[],
  product: ProductTypeSerializer
) => {
  const productInBasket = itemGroups.filter(
    (ig: TicketItemGroupType) => ig.product === product.id
  );

  return productInBasket.reduce<VariantQuantityMap>((acc, itemGroup) => {
    const variant = findVariant(product, itemGroup.product_attribute);

    // dont count item groups associated with a package as addons
    if (isProductPackageChild(itemGroup)) {
      return acc;
    }

    return {
      ...acc,
      [itemGroup.product_attribute]: {
        variant,
        quantity: itemGroup.quantity,
      },
    };
  }, {});
};

export const variantMapToQuantityMap = (variantMap: VariantQuantityMap) => {
  return Object.values(variantMap).reduce(
    (acc, { variant, quantity }) => ({
      ...acc,
      [variant.id]: quantity,
    }),
    {}
  );
};

const ProductHeadline = ({
  product,
  showCartTimer = true,
}: {
  product: ProductTypeSerializer;
  showCartTimer?: boolean;
}) => {
  const defaultImage = formatRelativeStaticPath(
    "images/default-showpass-square.png"
  );

  const { image, name } = product;
  return (
    <Flex justifyContent="space-between" bg="bg" py="md">
      <Box
        w="120px"
        h="120px"
        flexShrink={0}
        margin={{ base: "0px 16px 0px 20px", sm: "0px 16px 0px 24px" }}
      >
        <CardImage
          src={image || defaultImage}
          alt={`Product image for ${name}`}
          height={120}
          width={120}
          borderRadius="sm"
        />
      </Box>
      <Flex flex="1" minW="0">
        <Text fontWeight="bold" color="font.dark" size="lg" mt="1s">
          {name}
        </Text>
      </Flex>
      {showCartTimer && (
        <Box px="xl" flexShrink={0}>
          <CartTimer.Checkout />
        </Box>
      )}
    </Flex>
  );
};

export const ProductAddonsModalContent = ({
  product,
  showCartTimer = true,
  onChange,
  hideDescription,
}: ProductAddonsModalContentProps) => {
  const publicBasket = usePublicBasket();
  const { itemGroups } = publicBasket;
  const [variantQuantities, setVariantQuantities] =
    useState<VariantQuantityMap>({});

  useMountEffect(() => {
    setVariantQuantities(loadProductCountsFromBasket(itemGroups, product));
  });

  useEffect(() => {
    AnalyticsService.productViewed(product);
  }, [product]);

  const handleQuantityChange: AddonProductTabsProps["setVariantQuantityAdded"] =
    (getCounts) => {
      const counts =
        typeof getCounts === "function" ? getCounts({}) : getCounts;
      const key = Object.keys(counts)[0];
      const variant = findVariant(product, Number(key));

      if (!variant) {
        return;
      }

      setVariantQuantities((prev) => {
        return {
          ...prev,
          [key]: {
            variant,
            quantity: counts[key] || 0,
          },
        };
      });
    };

  useEffect(() => {
    onChange?.(variantQuantities);
  }, [variantQuantities]);

  return (
    <Flex direction="column" gap="none" overflowY="auto">
      <ProductHeadline product={product} showCartTimer={showCartTimer} />
      <AddonProductTabs
        product={product}
        variantQuantityAdded={variantMapToQuantityMap(variantQuantities)}
        setVariantQuantityAdded={handleQuantityChange}
        hideDescription={hideDescription}
      />
    </Flex>
  );
};
